import { formatInTimeZone as fmtInTz, utcToZonedTime, zonedTimeToUtc, } from "date-fns-tz";
import { format } from "date-fns";
// Map common US abbreviations to canonical IANA time zones
const ABBR_TO_IANA = {
    EDT: "America/New_York",
    EST: "America/New_York",
    CDT: "America/Chicago",
    CST: "America/Chicago",
    MDT: "America/Denver",
    MST: "America/Denver",
    PDT: "America/Los_Angeles",
    PST: "America/Los_Angeles",
    AKDT: "America/Anchorage",
    AKST: "America/Anchorage",
    HST: "Pacific/Honolulu",
};
function isIana(tz) {
    return typeof tz === "string" && tz.includes("/");
}
export function ensureIanaTimeZone(tzLike, fallback = "UTC") {
    try {
        if (!tzLike)
            return fallback;
        // If object with abbr or utc list
        if (typeof tzLike === "object") {
            if (tzLike.utc && Array.isArray(tzLike.utc) && tzLike.utc.length > 0) {
                return tzLike.utc[0];
            }
            if (tzLike.abbr && ABBR_TO_IANA[tzLike.abbr]) {
                return ABBR_TO_IANA[tzLike.abbr];
            }
            if (typeof tzLike.value === "string" && ABBR_TO_IANA[tzLike.value]) {
                return ABBR_TO_IANA[tzLike.value];
            }
        }
        if (typeof tzLike === "string") {
            if (isIana(tzLike))
                return tzLike;
            const upper = String(tzLike).toUpperCase();
            if (ABBR_TO_IANA[upper])
                return ABBR_TO_IANA[upper];
        }
    }
    catch (_e) { }
    return fallback;
}
export function utcToLocal(utcInput, tz) {
    const date = typeof utcInput === "string" ? new Date(utcInput) : utcInput;
    return utcToZonedTime(date, tz);
}
//  converts a wall‑time in a specific timezone into a UTC Date. It changes the actual moment to its UTC equivalent. It is used to convert dates to UTC for storage.
export function localToUtc(localIso, tz) {
    // Interpret localIso as wall time in tz and convert to UTC
    const input = localIso instanceof Date
        ? format(localIso, "yyyy-MM-dd'T'HH:mm:ss")
        : localIso;
    return zonedTimeToUtc(input, tz);
}
export function toZonedTime(dateInput, tz) {
    return utcToLocal(dateInput, tz);
}
export function toUtcFromZoned(localIso, tz) {
    return localToUtc(localIso, tz);
}
// formats a date/time for display in a specific timezone and returns a string. It does not change the underlying moment in time. It is used to format dates for display in the UI.
export function formatInTimeZone(dateInput, tz, pattern) {
    return fmtInTz(dateInput, tz, pattern);
}
/**
 * Return the calendar date (YYYY-MM-DD) for a given moment or date-only string
 * in the specified timezone. Use this for date-only business fields (e.g.
 * dateOfIssue, dueDate, datePaid) so they do not shift to the next day when
 * created late in the evening in timezones behind UTC (e.g. after 7:30 PM EST).
 *
 * @param dateInput - A Date, ISO string, or YYYY-MM-DD string
 * @param tz - IANA timezone (e.g. "America/New_York")
 * @returns YYYY-MM-DD string in that timezone
 */
export function getDateOnlyInTimezone(dateInput, tz) {
    if (!dateInput)
        return "";
    const s = typeof dateInput === "string" ? dateInput.trim() : "";
    if (s && /^\d{4}-\d{2}-\d{2}$/.test(s)) {
        return s;
    }
    const date = dateInput instanceof Date ? dateInput : new Date(dateInput);
    if (Number.isNaN(date.getTime()))
        return "";
    const zoned = utcToZonedTime(date, tz);
    return format(zoned, "yyyy-MM-dd");
}
/**
 * Determine whether a timestamp string has an explicit UTC offset of zero.
 * Examples treated as UTC:
 *  - 2025-01-01T00:00:00Z
 *  - 2025-01-01T00:00:00+00:00
 *  - 2025-01-01T00:00:00-00:00
 *  - 2025-01-01T00:00:00+0000
 */
export function isUtc(input) {
    if (!input)
        return false;
    const s = String(input);
    // Must have explicit zone information
    const hasZone = /[zZ]|[+\-]\d{2}:?\d{2}$/.test(s);
    if (!hasZone)
        return false;
    // Z suffix is UTC
    if (/[zZ]$/.test(s))
        return true;
    // Parse +HH:MM or +HHMM (also handles -00:00)
    const m = s.match(/([+\-]\d{2}):?(\d{2})$/);
    if (!m)
        return false;
    const hours = parseInt(m[1], 10); // includes sign
    const minutes = parseInt(m[2], 10);
    return hours === 0 && minutes === 0;
}
/**
 * Get the browser's IANA timezone, e.g. "America/Los_Angeles".
 * Falls back to 'UTC' when not available (e.g., non-browser or older runtimes).
 */
export function getBrowserTimezone(fallback = "UTC") {
    try {
        if (typeof Intl !== "undefined" &&
            typeof Intl.DateTimeFormat === "function") {
            const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
            if (tz && typeof tz === "string")
                return tz;
        }
    }
    catch (_err) {
        // ignore and use fallback
    }
    return fallback;
}
